import { CardsIcon, SquaresFourIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import { QueueView, rememberView } from './boardView'
import { SegmentedControl } from './SegmentedControl'
import { appendQueueNavSearch } from './locationParam'

const OPTIONS: { id: QueueView; label: string; icon: React.ReactElement; blurb: string }[] = [
  {
    id: `focus`,
    label: `One at a time`,
    icon: <CardsIcon size={14} weight="bold" />,
    blurb: `Work the queue a person at a time`,
  },
  {
    id: `board`,
    label: `Board`,
    icon: <SquaresFourIcon size={14} weight="bold" />,
    blurb: `Hold several conversations in flight at once`,
  },
]

/**
 * Which system pipeline a queue's board lives on, and which band within it.
 * The treatment queues share `system:treatment` and the hygiene queues share
 * `system:hygiene`, so a queue carries its band as its own param rather than
 * a pipeline id that doesn't exist. Only queues whose `surface` can resolve
 * to `board` need an entry.
 *
 * `band` is a plain string, not `TreatmentBandId | HygieneBandId`: importing
 * either band type here would close an import cycle through
 * `queueRegistry.ts`.
 */
const PIPELINE_TARGET: Record<string, { pipelineId: string; band?: string }> = {
  'active-leads': { pipelineId: `system:leads` },
  'hygiene-coming-due': { pipelineId: `system:hygiene`, band: `hygiene-coming-due` },
  'due-for-hygiene': { pipelineId: `system:hygiene`, band: `due-for-hygiene` },
  'hygiene-long-past-due': { pipelineId: `system:hygiene`, band: `hygiene-long-past-due` },
  'treatment-fresh': { pipelineId: `system:treatment`, band: `fresh` },
  'treatment-cooling': { pipelineId: `system:treatment`, band: `cooling` },
  'treatment-cold': { pipelineId: `system:treatment`, band: `cold` },
  'treatment-stale': { pipelineId: `system:treatment`, band: `stale` },
}

/**
 * Route for working a queue in a given view. The one place that maps a
 * `QueueView` onto its URL, so the toggle's click handler, `queueHref`, and
 * their tests agree on where each option goes instead of each hard-coding it.
 *
 * `focus` still opens the Action Center's own one-at-a-time route. `board`
 * now opens the Pipelines page on the queue's system pipeline (and band, for
 * treatment): boards render in exactly one place, and Pipelines is it.
 * `table` is the same destination carrying the board's own `?view=` param,
 * since the table is a second reading of one pipeline rather than a route of
 * its own.
 */
export function queueViewPath(view: QueueView, queueId: string): string {
  if (view === `focus`) return `/action-center/work/${queueId}`
  const target = PIPELINE_TARGET[queueId]
  // Defensive rather than a thrown assertion: a mismatch here is a wiring bug
  // in `PIPELINE_TARGET`, and landing on the bare Pipelines page is a far
  // gentler failure for it than crashing the card that linked here.
  if (!target) return `/pipelines`
  // Plain interpolation, not URLSearchParams: every other `?tab=` link in the
  // app (NavItem, NewPipeline, PipelineInfo) builds it the same unescaped
  // way, and a system pipeline id's `:` needs no escaping in a query value.
  const band = target.band ? `&band=${target.band}` : ``
  const table = view === `table` ? `&view=table` : ``
  return `/pipelines?tab=${target.pipelineId}${band}${table}`
}

/**
 * Which way this queue is being worked.
 *
 * A link between the two routes rather than local state, so the view is in the
 * URL and survives a reload, a bookmark and a share. The choice is remembered
 * per person so nobody re-picks it every morning.
 */
export function QueueViewToggle({
  queueId,
  userId,
  current,
  search,
}: {
  queueId: string
  userId?: string
  current: QueueView
  search: string
}) {
  const navigate = useNavigate()
  return (
    <SegmentedControl
      options={OPTIONS.map((option) => ({
        id: option.id,
        label: option.label,
        icon: option.icon,
        title: option.blurb,
      }))}
      value={current}
      onSelect={(id) => {
        // Recorded before the guard, deliberately. A click on the option you
        // are already on is still a person stating a preference, and they may
        // have arrived here by a link rather than by choosing. Skipping the
        // write would leave a stale preference that only a there-and-back
        // double click could ever correct.
        rememberView(userId, queueId, id)
        // Navigating to the URL you are already on still pushes a history
        // entry, and Back would then need two presses to leave the page.
        if (id === current) return
        navigate(appendQueueNavSearch(queueViewPath(id, queueId), search))
      }}
    />
  )
}
